import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type InvisalignMiamiFlLocalization = {
	title: string;
	pageTitle: string;
	intro: string;
	sections: Array<{
		heading: string;
		paragraphs?: string[];
		summaryItems?: Array<{ label: string; text: string }>;
		items?: Array<{ title: string; text: string }>;
	}>;
	faqHeading: string;
	faqs: Array<{ question: string; answer: string }>;
	finalHeading: string;
	finalParagraphs: string[];
};

const invisalignMiamiFlLocalization = {
	[Locale.EN]: {
		title: "Invisalign Miami FL",
		pageTitle: "Invisalign Clear Aligners in Miami, FL",
		intro:
			"Miami Orthodontic Specialists are the premier Invisalign providers in the Miami, FL area. Have you been thinking of orthodontic treatment? Invisalign is a great option for anyone looking for an alternative to traditional braces. We are here to help you determine if Invisalign is right for you.",
		sections: [
			{
				heading:
					"What Is Invisalign? Miami's Preferred Clear Aligner Treatment",
				paragraphs: [
					"Invisalign is an FDA-cleared orthodontic system that uses custom-made, clear, removable aligners to gradually shift your teeth into their ideal positions. No metal brackets. No wires. That's why Invisalign remains one of the most popular choices for anyone who wants a discreet way to straighten their teeth.",
					"Dr. Carmen Garcia-Paul at Miami Orthodontic Specialists uses advanced 3D digital treatment planning to map out your entire new smile before you even start. Each set of aligners is crafted from Invisalign's patented SmartTrack material, which applies gentle, consistent pressure to move teeth more predictably than earlier aligner technologies.",
					"The Invisalign system has been used by over 17 million people worldwide (Align Technology, 2024). As an experienced Invisalign provider in Miami, FL, Dr. Garcia-Paul combines this proven technology with her board-certified expertise in orthodontics and dentofacial orthopedics. She'll evaluate your specific needs during a free consult and help you understand exactly what Invisalign can do for your smile.",
					"If you're dealing with crowded teeth, gaps, or bite issues, clear aligners offer a modern approach to orthodontic treatment that fits right into your daily life.",
				],
			},
			{
				heading: "Benefits of Choosing Invisalign in Miami",
				paragraphs: [
					"Clear aligners at Miami Orthodontic Specialists offer several advantages for MOS family members who value both results and convenience. Here's a quick overview:",
				],
				summaryItems: [
					{ label: "Nearly invisible", text: "during normal conversation" },
					{ label: "Removable", text: "for eating, brushing, and flossing" },
					{ label: "Fewer office visits", text: "than traditional braces" },
					{
						label: "Comfortable",
						text: "smooth plastic with no brackets or wires",
					},
					{
						label: "Predictable",
						text: "with a digital preview of your results before you start",
					},
					{
						label: "Lifestyle-friendly",
						text: "for work, social events, and everything in between",
					},
				],
				items: [
					{
						title: "Nearly Invisible Appearance",
						text: "The clear plastic trays are difficult to notice during normal conversation.",
					},
					{
						title: "Removable for Eating and Oral Care",
						text: "No food restrictions. Pop your aligners out for meals.",
					},
					{
						title: "Fewer Office Visits",
						text: "Invisalign typically requires fewer check-in appointments than metal braces.",
					},
					{
						title: "How comfortable are they, really?",
						text: "Smooth plastic edges. No brackets. No wires.",
					},
					{
						title: "Predictable Results You Can Preview",
						text: "Before treatment starts, Dr. Garcia-Paul will show you a digital preview.",
					},
					{
						title: "Fits the Miami Lifestyle",
						text: "Clear trays fit into an active lifestyle.",
					},
				],
			},
			{
				heading: "The Invisalign Treatment Process at Our Miami Practice",
				paragraphs: [
					"Getting started with Invisalign at Miami Orthodontic Specialists is straightforward.",
				],
				items: [
					{
						title: "Step 1: Free Consult with Digital Scanning",
						text: "Your first step is a complimentary evaluation. Dr. Garcia-Paul will determine if Invisalign is right for you.",
					},
					{
						title: "Step 2: See Your Results Before You Start",
						text: "Dr. Garcia-Paul designs your customized treatment plan.",
					},
					{
						title: "Step 3: Receive Your Custom Aligners",
						text: "Your aligners are manufactured specifically for your teeth.",
					},
					{
						title: "Step 4: Progress Through Your Aligner Series",
						text: "You'll switch aligners every one to two weeks.",
					},
					{
						title: "Step 5: Finish Treatment and Keep Your Results",
						text: "After your final aligner, you'll receive retainers.",
					},
					{
						title: "Same-Day Start Available",
						text: "Qualifying patients can often get started the same day as their free consult.",
					},
				],
			},
			{
				heading: "Who Is a Good Candidate for Invisalign in Miami, FL?",
				paragraphs: [
					"Most teens and adults are good candidates for Invisalign.",
					"Clear aligners offer an effective solution that fits into your life.",
					"Some cases may benefit from metal braces.",
					"Ready to find out if Invisalign is right for you? Schedule your complimentary evaluation.",
				],
			},
			{
				heading: "Why Choose Miami Orthodontic Specialists for Invisalign",
				paragraphs: [
					"Dr. Carmen Garcia-Paul is a board-certified orthodontist with extensive Invisalign experience.",
					"We offer flexible payment options and will review costs during your free consult.",
				],
			},
		],
		faqHeading: "Frequently Asked Questions About Invisalign in Miami",
		faqs: [
			{
				question: "How much does Invisalign cost in Miami, FL?",
				answer:
					"During your free consult, we'll provide a detailed cost estimate.",
			},
			{
				question: "How long does Invisalign treatment take?",
				answer: "Most cases take 12 to 18 months.",
			},
			{
				question: "Does insurance cover Invisalign?",
				answer: "Many plans offer coverage.",
			},
			{
				question: "Is Invisalign painful?",
				answer: "Mild pressure is normal.",
			},
			{
				question: "Can I eat with Invisalign?",
				answer: "Remove aligners before eating.",
			},
			{
				question:
					"How often do I need to visit the orthodontist during treatment?",
				answer: "Visits are typically every six to eight weeks.",
			},
		],
		finalHeading: "Schedule Your Free Invisalign Consult in Miami Today",
		finalParagraphs: [
			"Your first visit is complimentary.",
			"Call us or request your free consult online.",
		],
	},
	[Locale.ES]: {
		title: "Invisalign Miami FL",
		pageTitle: "Alineadores transparentes Invisalign en Miami, FL",
		intro:
			"Miami Orthodontic Specialists es uno de los principales proveedores de Invisalign en el área de Miami, FL. ¿Has estado pensando en un tratamiento de ortodoncia? Invisalign es una excelente opción para quienes buscan una alternativa a los brackets tradicionales. Estamos aquí para ayudarte a determinar si Invisalign es adecuado para ti.",
		sections: [
			{
				heading:
					"¿Qué es Invisalign? El tratamiento con alineadores transparentes preferido en Miami",
				paragraphs: [
					"Invisalign es un sistema ortodóncico aprobado por la FDA que usa alineadores transparentes, removibles y hechos a la medida para mover gradualmente tus dientes a sus posiciones ideales. Sin brackets metálicos. Sin alambres. Por eso Invisalign sigue siendo una de las opciones más populares para quienes desean enderezar sus dientes de forma discreta.",
					"La Dra. Carmen Garcia-Paul en Miami Orthodontic Specialists usa planificación digital avanzada en 3D para diseñar tu nueva sonrisa completa antes de comenzar. Cada juego de alineadores se fabrica con el material SmartTrack patentado de Invisalign, que aplica presión suave y constante para mover los dientes de forma más predecible.",
					"El sistema Invisalign ha sido usado por más de 17 millones de personas en todo el mundo (Align Technology, 2024). Como proveedora experimentada de Invisalign en Miami, FL, la Dra. Garcia-Paul combina esta tecnología probada con su experiencia certificada en ortodoncia y ortopedia dentofacial. Evaluará tus necesidades específicas durante una consulta gratuita y te ayudará a entender exactamente qué puede hacer Invisalign por tu sonrisa.",
					"Si tienes dientes apiñados, espacios o problemas de mordida, los alineadores transparentes ofrecen un enfoque moderno para el tratamiento ortodóncico que se adapta a tu vida diaria.",
				],
			},
			{
				heading: "Beneficios de elegir Invisalign en Miami",
				paragraphs: [
					"Los alineadores transparentes en Miami Orthodontic Specialists ofrecen varias ventajas para los miembros de la familia MOS que valoran tanto los resultados como la comodidad. Aquí tienes un resumen:",
				],
				summaryItems: [
					{ label: "Casi invisibles", text: "durante una conversación normal" },
					{
						label: "Removibles",
						text: "para comer, cepillarte y usar hilo dental",
					},
					{
						label: "Menos visitas a la oficina",
						text: "que con los brackets tradicionales",
					},
					{
						label: "Cómodos",
						text: "plástico suave, sin brackets ni alambres",
					},
					{
						label: "Predecibles",
						text: "con una vista previa digital de tus resultados antes de empezar",
					},
					{
						label: "Ideales para tu estilo de vida",
						text: "para el trabajo, eventos sociales y todo lo demás",
					},
				],
				items: [
					{
						title: "Apariencia casi invisible",
						text: "Las bandejas transparentes de plástico son difíciles de notar durante una conversación normal.",
					},
					{
						title: "Removibles para comer y cuidar la higiene oral",
						text: "Sin restricciones de comida. Retira tus alineadores para comer.",
					},
					{
						title: "Menos visitas a la oficina",
						text: "Invisalign generalmente requiere menos citas de control que los brackets metálicos.",
					},
					{
						title: "¿Qué tan cómodos son realmente?",
						text: "Bordes de plástico suaves. Sin brackets. Sin alambres.",
					},
					{
						title: "Resultados predecibles que puedes ver antes",
						text: "Antes de comenzar el tratamiento, la Dra. Garcia-Paul te mostrará una vista previa digital.",
					},
					{
						title: "Se adapta al estilo de vida de Miami",
						text: "Los alineadores transparentes se ajustan a una vida activa.",
					},
				],
			},
			{
				heading:
					"El proceso de tratamiento Invisalign en nuestra práctica de Miami",
				paragraphs: [
					"Comenzar con Invisalign en Miami Orthodontic Specialists es sencillo.",
				],
				items: [
					{
						title: "Paso 1: Consulta gratuita con escaneo digital",
						text: "Tu primer paso es una evaluación gratuita. La Dra. Garcia-Paul determinará si Invisalign es adecuado para ti.",
					},
					{
						title: "Paso 2: Mira tus resultados antes de empezar",
						text: "La Dra. Garcia-Paul diseña tu plan de tratamiento personalizado.",
					},
					{
						title: "Paso 3: Recibe tus alineadores personalizados",
						text: "Tus alineadores se fabrican específicamente para tus dientes.",
					},
					{
						title: "Paso 4: Avanza a través de tu serie de alineadores",
						text: "Cambiarás de alineadores cada una o dos semanas.",
					},
					{
						title: "Paso 5: Termina el tratamiento y conserva tus resultados",
						text: "Después de tu último alineador, recibirás retenedores.",
					},
					{
						title: "Inicio el mismo día disponible",
						text: "Los pacientes que califican a menudo pueden comenzar el mismo día de su consulta gratuita.",
					},
				],
			},
			{
				heading: "¿Quién es buen candidato para Invisalign en Miami, FL?",
				paragraphs: [
					"La mayoría de adolescentes y adultos son buenos candidatos para Invisalign.",
					"Los alineadores transparentes ofrecen una solución eficaz que se adapta a tu vida.",
					"Algunos casos pueden beneficiarse de brackets metálicos.",
					"¿Listo para saber si Invisalign es adecuado para ti? Programa tu evaluación gratuita.",
				],
			},
			{
				heading: "Por qué elegir Miami Orthodontic Specialists para Invisalign",
				paragraphs: [
					"La Dra. Carmen Garcia-Paul es una ortodoncista certificada por la junta con amplia experiencia en Invisalign.",
					"Ofrecemos opciones de pago flexibles y revisaremos los costos durante tu consulta gratuita.",
				],
			},
		],
		faqHeading: "Preguntas frecuentes sobre Invisalign en Miami",
		faqs: [
			{
				question: "¿Cuánto cuesta Invisalign en Miami, FL?",
				answer:
					"Durante tu consulta gratuita, te daremos un estimado detallado del costo.",
			},
			{
				question: "¿Cuánto dura el tratamiento con Invisalign?",
				answer: "La mayoría de los casos toma de 12 a 18 meses.",
			},
			{
				question: "¿El seguro cubre Invisalign?",
				answer: "Muchos planes ofrecen cobertura.",
			},
			{
				question: "¿Invisalign duele?",
				answer: "Es normal sentir una presión leve.",
			},
			{
				question: "¿Puedo comer con Invisalign?",
				answer: "Retira los alineadores antes de comer.",
			},
			{
				question:
					"¿Con qué frecuencia debo visitar al ortodoncista durante el tratamiento?",
				answer: "Las visitas suelen ser cada seis a ocho semanas.",
			},
		],
		finalHeading: "Programa tu consulta gratuita de Invisalign en Miami hoy",
		finalParagraphs: [
			"Tu primera visita es gratuita.",
			"Llámanos o solicita tu consulta gratuita en línea.",
		],
	},
} satisfies Record<Locale, InvisalignMiamiFlLocalization>;

export function InvisalignMiamiFlPage() {
	const getLocalization = useLocalization(invisalignMiamiFlLocalization);
	const sections = getLocalization("sections");
	const faqs = getLocalization("faqs");
	const isEnglish =
		getLocalization("pageTitle") === "Invisalign Clear Aligners in Miami, FL";

	const linkedCopy = (text: string) => {
		if (isEnglish) {
			if (text === getLocalization("intro")) {
				return (
					<>
						<Link to="/" className={styles.inlineLink}>
							Miami Orthodontic Specialists
						</Link>{" "}
						are the premier Invisalign providers in the Miami, FL area. Have you
						been thinking of orthodontic treatment?{" "}
						<Link to="/invisalign-miami" className={styles.inlineLink}>
							Invisalign
						</Link>{" "}
						is a great option for anyone looking for an alternative to{" "}
						<Link to="/life-with-braces" className={styles.inlineLink}>
							traditional braces
						</Link>
						. We are here to help you determine if{" "}
						<Link to="/invisalign-miami" className={styles.inlineLink}>
							Invisalign
						</Link>{" "}
						is right for you.
					</>
				);
			}

			switch (text) {
				case "Invisalign is an FDA-cleared orthodontic system that uses custom-made, clear, removable aligners to gradually shift your teeth into their ideal positions. No metal brackets. No wires. That's why Invisalign remains one of the most popular choices for anyone who wants a discreet way to straighten their teeth.":
					return (
						<>
							<Link to="/invisalign" className={styles.inlineLink}>
								Invisalign
							</Link>{" "}
							is an FDA-cleared orthodontic system that uses custom-made, clear,
							removable aligners to gradually shift your teeth into their ideal
							positions. No metal brackets. No wires. That's why{" "}
							<Link to="/invisalign" className={styles.inlineLink}>
								Invisalign
							</Link>{" "}
							remains one of the most popular choices for anyone who wants a
							discreet way to straighten their teeth.
						</>
					);
				case "The Invisalign system has been used by over 17 million people worldwide (Align Technology, 2024). As an experienced Invisalign provider in Miami, FL, Dr. Garcia-Paul combines this proven technology with her board-certified expertise in orthodontics and dentofacial orthopedics. She'll evaluate your specific needs during a free consult and help you understand exactly what Invisalign can do for your smile.":
					return (
						<>
							The{" "}
							<Link to="/invisalign" className={styles.inlineLink}>
								Invisalign
							</Link>{" "}
							system has been used by over 17 million people worldwide (Align
							Technology, 2024). As an experienced Invisalign provider in Miami,
							FL, Dr. Garcia-Paul combines this proven technology with her
							board-certified expertise in orthodontics and dentofacial
							orthopedics. She&apos;ll evaluate your specific needs during a{" "}
							<Link to="/free-consult" className={styles.inlineLink}>
								free consult
							</Link>{" "}
							and help you understand exactly what Invisalign can do for your
							smile.
						</>
					);
				case "If you're dealing with crowded teeth, gaps, or bite issues, clear aligners offer a modern approach to orthodontic treatment that fits right into your daily life.":
					return (
						<>
							If you&apos;re dealing with crowded teeth, gaps, or bite issues,{" "}
							<Link to="/invisalign" className={styles.inlineLink}>
								clear aligners
							</Link>{" "}
							offer a modern approach to orthodontic treatment that fits right
							into your daily life.
						</>
					);
				case "Clear aligners at Miami Orthodontic Specialists offer several advantages for MOS family members who value both results and convenience. Here's a quick overview:":
					return (
						<>
							<Link to="/invisalign" className={styles.inlineLink}>
								Clear aligners
							</Link>{" "}
							at Miami Orthodontic Specialists offer several advantages for MOS
							family members who value both results and convenience. Here&apos;s
							a quick overview:
						</>
					);
				case "Invisalign typically requires fewer check-in appointments than metal braces.":
					return (
						<>
							<Link to="/invisalign" className={styles.inlineLink}>
								Invisalign
							</Link>{" "}
							typically requires fewer check-in appointments than{" "}
							<Link to="/metal-braces" className={styles.inlineLink}>
								metal braces
							</Link>
							.
						</>
					);
				case "Getting started with Invisalign at Miami Orthodontic Specialists is straightforward.":
					return (
						<>
							Getting started with{" "}
							<Link to="/invisalign" className={styles.inlineLink}>
								Invisalign
							</Link>{" "}
							at Miami Orthodontic Specialists is straightforward.
						</>
					);
				case "Your first step is a complimentary evaluation. Dr. Garcia-Paul will determine if Invisalign is right for you.":
					return (
						<>
							Your first step is a complimentary evaluation. Dr. Garcia-Paul
							will determine if{" "}
							<Link to="/invisalign" className={styles.inlineLink}>
								Invisalign
							</Link>{" "}
							is right for you.
						</>
					);
				case "Qualifying patients can often get started the same day as their free consult.":
					return (
						<>
							Qualifying patients can often get started the same day as their{" "}
							<Link to="/free-consult" className={styles.inlineLink}>
								free consult
							</Link>
							.
						</>
					);
				case "Most teens and adults are good candidates for Invisalign.":
					return (
						<>
							Most teens and adults are good candidates for{" "}
							<Link to="/invisalign" className={styles.inlineLink}>
								Invisalign
							</Link>
							.
						</>
					);
				case "Some cases may benefit from metal braces.":
					return (
						<>
							Some cases may benefit from{" "}
							<Link to="/metal-braces" className={styles.inlineLink}>
								metal braces
							</Link>
							.
						</>
					);
				case "We offer flexible payment options and will review costs during your free consult.":
					return (
						<>
							We offer flexible payment options and will review costs during
							your{" "}
							<Link to="/free-consult" className={styles.inlineLink}>
								free consult
							</Link>
							.
						</>
					);
				case "During your free consult, we'll provide a detailed cost estimate.":
					return (
						<>
							During your{" "}
							<Link to="/free-consult" className={styles.inlineLink}>
								free consult
							</Link>
							, we&apos;ll provide a detailed cost estimate.
						</>
					);
				case "Call us or request your free consult online.":
					return (
						<>
							Call us or request your{" "}
							<Link to="/free-consult" className={styles.inlineLink}>
								free consult
							</Link>{" "}
							online.
						</>
					);
				default:
					return text;
			}
		}

		if (text === getLocalization("intro")) {
			return (
				<>
					<Link to="/" className={styles.inlineLink}>
						Miami Orthodontic Specialists
					</Link>{" "}
					es uno de los principales proveedores de Invisalign en el área de
					Miami, FL. ¿Has estado pensando en un tratamiento de ortodoncia?{" "}
					<Link to="/invisalign-miami" className={styles.inlineLink}>
						Invisalign
					</Link>{" "}
					es una excelente opción para quienes buscan una alternativa a los{" "}
					<Link to="/life-with-braces" className={styles.inlineLink}>
						brackets tradicionales
					</Link>
					. Estamos aquí para ayudarte a determinar si{" "}
					<Link to="/invisalign-miami" className={styles.inlineLink}>
						Invisalign
					</Link>{" "}
					es adecuado para ti.
				</>
			);
		}

		return text;
	};

	return (
		<Flex direction="column">
		<HeroStyle2
				showFullImageOnMobile
				heroImageUrl="/miami-ortho-19.jpg"
				title={getLocalization("title")}
				backgroundPosition="center"
			/>
			<Box className={styles.invisalignFlBand}>
				<Box className={styles.invisalignFlInner}>
					<Heading as="h2" className={styles.invisalignFlPageTitle}>
						{getLocalization("pageTitle")}
					</Heading>
					<Text as="p">{linkedCopy(getLocalization("intro"))}</Text>
					<OptimizedImage
						alt="invisalign miami fl"
						className={styles.invisalignFlFeatureImage}
						height={1707}
						src="/invisalign-miami-fl-001-scaled.jpg"
						width={2560}
					/>
				</Box>
			</Box>
			<Box className={styles.invisalignFlBand}>
				<Box className={styles.invisalignFlInner}>
					{sections.map((section) => (
						<section
							className={styles.invisalignFlContentSection}
							key={section.heading}
						>
							<Heading as="h2" className={styles.invisalignFlSectionHeading}>
								{section.heading}
							</Heading>
							{section.paragraphs?.map((paragraph) => (
								<Text as="p" key={paragraph}>
									{linkedCopy(paragraph)}
								</Text>
							))}
							{section.summaryItems ? (
								<ul className={styles.invisalignFlSummaryList}>
									{section.summaryItems.map((item) => (
										<li key={item.label}>
											<strong>{item.label}</strong> {item.text}
										</li>
									))}
								</ul>
							) : null}
							{section.items ? (
								<Box className={styles.invisalignFlItemList}>
									{section.items.map((item) => (
										<Box className={styles.invisalignFlItem} key={item.title}>
											<Heading
												as="h3"
												className={styles.invisalignFlItemHeading}
											>
												{item.title}
											</Heading>
											<Text as="p">{linkedCopy(item.text)}</Text>
										</Box>
									))}
								</Box>
							) : null}
						</section>
					))}
					<section className={styles.invisalignFlContentSection}>
						<Heading as="h2" className={styles.invisalignFlSectionHeading}>
							{getLocalization("faqHeading")}
						</Heading>
						<Box className={styles.invisalignFlItemList}>
							{faqs.map((faq) => (
								<Box className={styles.invisalignFlItem} key={faq.question}>
									<Heading as="h3" className={styles.invisalignFlItemHeading}>
										{faq.question}
									</Heading>
									<Text as="p">{linkedCopy(faq.answer)}</Text>
								</Box>
							))}
						</Box>
					</section>
					<section className={styles.invisalignFlContentSection}>
						<Heading as="h2" className={styles.invisalignFlSectionHeading}>
							{getLocalization("finalHeading")}
						</Heading>
						{getLocalization("finalParagraphs").map((paragraph) => (
							<Text as="p" key={paragraph}>
								{linkedCopy(paragraph)}
							</Text>
						))}
					</section>
				</Box>
			</Box>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
